import { useEffect, useRef } from "react";
import { useAuth } from "./useAuth";
import { UserType } from "@/types/user";
import { UserService } from "@/services/user/userService";
import { AdminService } from "@/services/admin/adminService";

/**
 * Hook to send heartbeat/activity updates to the server
 * This helps track which users are actually online (not just logged in)
 */
export const useActivity = () => {
  const { user, isAuthenticated } = useAuth();
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    // Early return if user is not authenticated - prevents errors for non-logged-in users
    if (!isAuthenticated || !user) {
      // Clear any existing interval if user logs out or is not authenticated
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
      return;
    }

    // Send initial activity update
    const sendActivity = async () => {
      try {
        // Use appropriate service based on user type
        if (user.type === UserType.Admin) {
          await AdminService.updateActivity();
        } else {
          await UserService.updateActivity();
        }
      } catch (error: any) {
        // Silently fail for 401 errors (token refresh will be handled by interceptor)
        // Only log other errors
        if (error?.status !== 401) {
        }
      }
    };

    // Send immediately on mount/login
    sendActivity();

    // Set up interval to send activity every 2 minutes
    intervalRef.current = setInterval(
      () => {
        sendActivity();
      },
      2 * 60 * 1000,
    ); // 2 minutes

    // Cleanup on unmount or when user changes
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [isAuthenticated, user]);
};
